<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateFinanceStatusBoletoSection from './RealEstateFinanceStatusBoletoSection.vue';
import RealEstateFinanceStatusPaymentSection from './RealEstateFinanceStatusPaymentSection.vue';

const props = defineProps({
  installment: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  statuses: { type: Array, default: () => [] },
});

const emit = defineEmits(['back', 'boleto', 'payment', 'submit']);
const { t } = useI18n();
const observation = ref('');
const targetStatus = ref('');
const detail = computed(() => props.installment || {});
const currentStatus = computed(() => detail.value.parcela?.status || {});
const responsibilities = computed(
  () => detail.value.parcela?.responsaveis_pagamento || []
);
const statusOptions = computed(() =>
  props.statuses.filter(status => status.slug !== 'cancelado')
);
const changingToBoleto = computed(
  () => targetStatus.value === 'boleto_enviado'
);
const changingToPayment = computed(() => targetStatus.value === 'pago');
const hasCompleteBoleto = responsibility =>
  Boolean(
    responsibility.boleto?.enviado &&
      responsibility.data_emissao &&
      responsibility.data_envio &&
      responsibility.data_validade
  );
const hasAnyCompleteBoleto = computed(() =>
  responsibilities.value.some(hasCompleteBoleto)
);
const hasEveryCompleteBoleto = computed(
  () =>
    responsibilities.value.length > 0 &&
    responsibilities.value.every(hasCompleteBoleto)
);
const paymentsConfirmed = computed(
  () =>
    responsibilities.value.length > 0 &&
    responsibilities.value.every(
      responsibility => responsibility.comfirmacao_pagamento === 1
    )
);
const canSubmit = computed(() => {
  if (!targetStatus.value || props.loading) return false;
  if (changingToBoleto.value) {
    return responsibilities.value.length > 0 && hasAnyCompleteBoleto.value;
  }
  if (changingToPayment.value) {
    return hasEveryCompleteBoleto.value && paymentsConfirmed.value;
  }
  return true;
});

const statusClass = color =>
  ({
    neutral: 'bg-slate-100 text-slate-700',
    yellow: 'bg-yellow-100 text-yellow-800',
    primary: 'bg-blue-100 text-blue-800',
    green: 'bg-emerald-100 text-emerald-800',
    red: 'bg-red-100 text-red-800',
  })[color] || 'bg-slate-100 text-slate-700';
const submit = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    observacao: observation.value.trim(),
    target_status: targetStatus.value,
  });
};

watch(
  () => detail.value.parcela?.parcela_id,
  () => {
    observation.value = '';
    targetStatus.value = '';
  }
);
</script>

<template>
  <form
    data-testid="finance-status-update-form"
    class="mx-auto flex w-full max-w-[1042px] flex-col gap-5 pb-28"
    @submit.prevent="submit"
  >
    <section
      class="rounded-lais border border-lais-border-soft bg-white p-6 shadow-lais-card sm:p-8"
    >
      <h2 class="font-redHatDisplay text-xl font-semibold text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.TITLE') }}
      </h2>
      <div class="mt-5 flex flex-wrap items-center gap-3">
        <span class="text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.CURRENT') }}
        </span>
        <span
          class="rounded-full px-3 py-1 text-xs font-bold"
          :class="statusClass(currentStatus.color)"
        >
          {{
            currentStatus.label || t('REAL_ESTATE_FINANCE.TABLE.NOT_INFORMED')
          }}
        </span>
      </div>
      <LaisField
        class="mt-6 max-w-[391px]"
        :label="t('REAL_ESTATE_FINANCE.STATUS_UPDATE.FIELD')"
        required
      >
        <select
          v-model="targetStatus"
          data-testid="finance-status-update-select"
          class="min-h-11 w-full rounded-lais border border-lais-border bg-white px-3 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
        >
          <option value="">
            {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.SELECT') }}
          </option>
          <option
            v-for="status in statusOptions"
            :key="status.slug"
            :value="status.slug"
          >
            {{ status.label }}
          </option>
        </select>
      </LaisField>
      <p
        v-if="changingToBoleto && !responsibilities.length"
        class="mt-5 rounded-lais border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
      >
        {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.NO_PAYERS') }}
      </p>
      <p
        v-if="changingToPayment && !hasEveryCompleteBoleto"
        class="mt-5 rounded-lais border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
      >
        {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.PAYMENT_WARNING') }}
      </p>
    </section>

    <RealEstateFinanceStatusBoletoSection
      v-if="changingToBoleto && responsibilities.length"
      :loading="loading"
      :responsibilities="responsibilities"
      @boleto="emit('boleto', $event)"
    />

    <RealEstateFinanceStatusPaymentSection
      v-if="changingToPayment && hasAnyCompleteBoleto"
      :loading="loading"
      :responsibilities="responsibilities"
      @boleto="emit('boleto', $event)"
      @payment="emit('payment', $event)"
    />

    <section
      class="rounded-lais border border-lais-border-soft bg-white p-6 shadow-lais-card sm:p-8"
    >
      <div class="max-w-[620px]">
        <h2 class="font-redHatDisplay text-xl font-semibold text-lais-text">
          {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.OBSERVATION_TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.OBSERVATION_HELP') }}
        </p>
      </div>
      <LaisField
        class="mt-6"
        :label="t('REAL_ESTATE_FINANCE.STATUS_UPDATE.OBSERVATION_LABEL')"
      >
        <textarea
          v-model="observation"
          data-testid="finance-status-update-observation"
          :placeholder="
            t('REAL_ESTATE_FINANCE.STATUS_UPDATE.OBSERVATION_PLACEHOLDER')
          "
          rows="3"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-sm text-lais-text focus:border-lais-primary focus:outline-none"
        />
      </LaisField>
    </section>

    <footer
      class="fixed inset-x-0 bottom-0 z-lais-raised border-t border-lais-border bg-white px-4 py-4 sm:px-6"
    >
      <div
        class="mx-auto flex w-full max-w-[1042px] flex-wrap justify-end gap-3"
      >
        <LaisPrimaryButton type="button" variant="ghost" @click="emit('back')">
          {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.BACK') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          data-testid="finance-status-update-submit"
          :disabled="!canSubmit"
          :is-loading="loading"
          type="submit"
        >
          {{ t('REAL_ESTATE_FINANCE.STATUS_UPDATE.SAVE') }}
          <span class="i-lucide-arrow-right size-4" aria-hidden="true" />
        </LaisPrimaryButton>
      </div>
    </footer>
  </form>
</template>
